import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../components/consts/routes.consts";
import metaWalletCard from "../../assets/images/svg/meta-wallet-card.svg";
import metaWalletCardMobile from "../../assets/images/svg/meta-wallet-card-mobile.svg";
import {
  useCancelSubscriptionMutation,
  useChangeSubscriptionPlanMutation,
  useCreatePortalSessionMutation,
  useGetBillingDetailsQuery,
  useGetMetaBuilderAuthStatusQuery,
  useGetSubscriptionQuery,
  useUpdateBillingDetailsMutation,
} from "../../components/apis/metaBuilderApi";
import { toast } from "react-toastify";
import MetaAdsChangePlanModal from "../../components/metaAds/MetaAdsChangePlanModal";
import MetaAdsCancelPlanModal from "../../components/metaAds/MetaAdsCancelPlanModal";
import MetaAdsUpdateBillingModal from "../../components/metaAds/MetaAdsUpdateBillingModal";
import MetaAdsBillingDetails, {
  MetaAdsInvoice,
} from "../../components/metaAds/MetaAdsBillingDetails";

export default function MetaAdsWallet() {
  const navigate = useNavigate();
  const [isChangePlanModalOpen, setIsChangePlanModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isUpdateBillingModalOpen, setIsUpdateBillingModalOpen] =
    useState(false);
  const [showBillingDetails, setShowBillingDetails] = useState(false);
  const { data: authStatusData } = useGetMetaBuilderAuthStatusQuery();
  const metaUserId = authStatusData?.userId || localStorage.getItem("id") || "";

  // Show toast if user was redirected from protected page
  React.useEffect(() => {
    const showSubscriptionToast = localStorage.getItem("showSubscriptionRequiredToast");
    if (showSubscriptionToast === "true") {
      toast.info("Subscription not active. Please subscribe to access this feature.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        pauseOnHover: false,
        pauseOnFocusLoss: false,
      });
      // Clear the flag after showing toast
      localStorage.removeItem("showSubscriptionRequiredToast");
    }
  }, []);
  const [changeSubscriptionPlan, { isLoading: isPlanChangeLoading }] =
    useChangeSubscriptionPlanMutation();
  const [cancelSubscription, { isLoading: isCancelLoading }] =
    useCancelSubscriptionMutation();
  const [updateBillingDetails, { isLoading: isBillingUpdating }] =
    useUpdateBillingDetailsMutation();
  const [createPortalSession, { isLoading: isCreatingPortalSession }] =
    useCreatePortalSessionMutation();
  const { data: subscriptionData } = useGetSubscriptionQuery(metaUserId, {
    skip: !metaUserId,
  });
  const { data: billingData, isFetching: isBillingFetching } =
    useGetBillingDetailsQuery(metaUserId, {
      skip: !metaUserId || !showBillingDetails,
    });

  const subscriptionStatus =
    subscriptionData?.subscription?.status?.toLowerCase();
  const subscriptionPackageName =
    subscriptionData?.subscription?.packageName || "";
  const hasActiveSubscription =
    Boolean(subscriptionData?.subscription) &&
    !subscriptionData?.expired &&
    (subscriptionStatus === "active" || subscriptionStatus === "trialing");
  const isAdminAccessPlan = subscriptionPackageName === "Admin Access";
  const cancelAtPeriodEnd =
    subscriptionData?.subscription?.cancelAtPeriodEnd || false;
  const shouldShowBillingDetailsCta =
    hasActiveSubscription && !cancelAtPeriodEnd && !isAdminAccessPlan;

  const storagePlanType = (() => {
    try {
      const raw = localStorage.getItem("metaAdsPlanPurchased");
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { plan?: "monthly" | "yearly" };
      return parsed.plan ?? null;
    } catch {
      return null;
    }
  })();

  const planType: "monthly" | "yearly" =
    String(
      subscriptionData?.subscription?.type || storagePlanType || "monthly",
    ).toLowerCase() === "yearly"
      ? "yearly"
      : "monthly";
  const planLabel =
    planType === "yearly" ? "Yearly - $100/year" : "Monthly - $9/month";
  const subscriptionEndDate = (() => {
    const raw = subscriptionData?.subscription?.endDate;
    if (!raw) return null;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    date.setDate(date.getDate() - 1);
    return date;
  })();
  const renewDate = subscriptionEndDate?.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const accessUntilDate = subscriptionEndDate
    ? `${String(subscriptionEndDate.getDate()).padStart(2, "0")}.${String(
        subscriptionEndDate.getMonth() + 1,
      ).padStart(2, "0")}.${subscriptionEndDate.getFullYear()}`
    : null;
  const isCanceledSubscriptionState =
    hasActiveSubscription && cancelAtPeriodEnd;
  const primaryCtaLabel = shouldShowBillingDetailsCta
    ? "Billing details"
    : cancelAtPeriodEnd
      ? "Renew Subscription"
      : "Explore now!";

  const handleConfirmPlanChange = async () => {
    const userId = metaUserId;
    if (!userId) {
      toast.error("Please connect your account first.");
      return;
    }

    const targetPlan: "monthly" | "yearly" =
      planType === "monthly" ? "yearly" : "monthly";

    try {
      const result = await changeSubscriptionPlan({
        userId,
        newPackageType: targetPlan,
        packageName: "Meta Ads Builder",
      }).unwrap();

      if (result?.success) {
        toast.success(`Successfully changed to ${targetPlan} plan!`);
        setIsChangePlanModalOpen(false);
        // The subscription query will be automatically refetched due to invalidatesTags
      } else {
        toast.error(result?.message || "Failed to change plan.");
      }
    } catch (err: any) {
      const msg = err?.data?.error || err?.message || "Plan change failed.";
      toast.error(msg);
    }
  };

  const handleCancelSubscription = async () => {
    const userId = metaUserId;
    if (!userId) {
      toast.error("Please connect your account first.");
      return;
    }

    try {
      const result = await cancelSubscription({
        userId,
        cancelAtPeriodEnd: true,
      }).unwrap();

      if (result?.success) {
        toast.success(
          result?.message ||
            "Subscription will be canceled at the end of the current billing period.",
        );
        setIsCancelModalOpen(false);
      } else {
        toast.error(result?.message || "Failed to cancel subscription.");
      }
    } catch (err: any) {
      const msg = err?.data?.error || err?.message || "Cancellation failed.";
      toast.error(msg);
    }
  };

  const handleUpdateBilling = async (data: {
    billingName: string;
    email: string;
  }) => {
    const userId = metaUserId;
    if (!userId) {
      toast.error("Please connect your account first.");
      return;
    }

    try {
      const result = await updateBillingDetails({
        userId,
        ...data,
      }).unwrap();

      if (result?.success) {
        toast.success(
          result?.message || "Billing details updated successfully!",
        );
        setIsUpdateBillingModalOpen(false);
      } else {
        toast.error("Failed to update billing details.");
      }
    } catch (err: any) {
      const msg = err?.data?.error || err?.message || "Update failed.";
      toast.error(msg);
    }
  };

  const handleRenewSubscription = async () => {
    const userId = metaUserId;
    if (!userId) {
      toast.error("Please connect your account first.");
      return;
    }

    try {
      const result = await createPortalSession({
        userId,
        returnUrl: window.location.href,
      }).unwrap();

      window.location.href = result.url;
    } catch (err: any) {
      const msg =
        err?.data?.error || err?.message || "Failed to open billing portal.";
      toast.error(msg);
    }
  };

  const adminAccessEndDateLabel = subscriptionEndDate?.toLocaleDateString(
    "en-US",
    {
      month: "short",
      day: "numeric",
      year: "numeric",
    },
  );

  return (
    <div className="xl:inline-block xl:w-[calc(100%-320px)] lg:mt-0 mt-6  pb-8 pt-6">
      <div className="w-full max-w-[1320px] mx-auto lg:px-1 ">
        {!showBillingDetails && (
          <h1 className="text-white text-[24px] lg:text-[36px] font-bold leading-[1.15]">
            Your wallet
          </h1>
        )}

        {!showBillingDetails && (
          <div
            className="mt-6 lg:mt-10 rounded-[24px] lg:rounded-[40px] p-[2px]"
            style={{
              background:
                "linear-gradient(0deg, #FFFFFF 0%, #001AFF 44.71%, #1B76FF 100%)",
            }}
          >
            <>
              {/* Mobile Layout */}
              <div className="lg:hidden rounded-[22px] bg-[#06070C] overflow-hidden relative flex flex-col">
                {/* Section 1: Title and Button */}
                <div className="px-5 pt-4 pb-0 space-y-4">
                  <h2 className="m-0 text-white text-[20px] font-semibold leading-[1.15]">
                    Meta ads builder
                  </h2>
                  {!isAdminAccessPlan && (
                    <button
                      type="button"
                      onClick={() => {
                        if (shouldShowBillingDetailsCta) {
                          setShowBillingDetails(true);
                        } else if (cancelAtPeriodEnd) {
                          handleRenewSubscription();
                        } else {
                          navigate(ROUTES.META_ADS_SETUP_PUBLIC);
                        }
                      }}
                      disabled={isCreatingPortalSession}
                      className="w-full px-[24px] py-[14px] rounded-[9999px] bg-white text-black text-[14px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {primaryCtaLabel}
                    </button>
                  )}
                </div>

                {/* Section 2: Wallet Card Image */}
                <div className="flex justify-end overflow-hidden">
                  <img
                    src={metaWalletCardMobile}
                    alt=""
                    role="presentation"
                    className="w-[280px] h-[200px] object-contain mr-[-40px]"
                  />
                </div>

                {/* Section 3: Text Content */}
                <div className="px-5 pb-5">
                  {hasActiveSubscription ? (
                    <div className="max-w-full">
                      {isAdminAccessPlan ? (
                        <div className="space-y-2">
                          <p className="text-white text-[14px] leading-[1.35]">
                            <span className="font-bold">Current plan:</span>{" "}
                            <span className="font-bold">Custom</span>
                          </p>
                          {adminAccessEndDateLabel && (
                            <p className="text-white text-[14px] font-medium leading-[1.35]">
                              Ends on {adminAccessEndDateLabel}
                            </p>
                          )}
                        </div>
                      ) : cancelAtPeriodEnd ? (
                        <div className="space-y-3">
                          <div className="text-white leading-[1.55]">
                            <div className="text-[13px]">
                              <p>
                                Your subscription has been canceled. You can
                                renew it anytime without charge to continue
                                using it in the next billing period.
                              </p>
                            </div>
                          </div>
                          {accessUntilDate && (
                            <p className="text-white text-[14px] leading-[1.55]">
                              You can continue using the tool until{" "}
                              <span>{accessUntilDate}</span>
                            </p>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2 mb-3">
                            <p className="text-white text-[14px] leading-[1.35]">
                              <span className="font-bold">Current plan:</span>{" "}
                              <span className="font-bold">{planLabel}</span>
                            </p>
                            {renewDate && (
                              <p className="text-white text-[14px] font-medium leading-[1.35]">
                                Automatically renew on {renewDate}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center px-1 pb-2 justify-between">
                            <button
                              type="button"
                              onClick={() => setIsChangePlanModalOpen(true)}
                              className="text-[#58F2A9] text-[16px] leading-[1.3] font-semibold"
                            >
                              Change plan
                            </button>
                            <span className="w-px h-5 bg-white/20 w-[2px]" />
                            <button
                              type="button"
                              onClick={() => setIsCancelModalOpen(true)}
                              className="text-[#FF2D2D] text-[16px] leading-[1.3] font-semibold"
                            >
                              Cancel plan
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <p className="text-white text-[14px] leading-[1.6] font-medium mb-[6px]">
                      Make ad management effortless with Meta Ads Builder and
                      reach new listeners using music industry - trusted
                      strategy!
                    </p>
                  )}
                </div>
              </div>
              {/* Desktop Layout - Unchanged */}
              <div className="hidden lg:block rounded-[38px] bg-[#06070C] overflow-hidden relative lg:h-[422px]">
                <div className="absolute inset-0 pointer-events-none">
                  <img
                    src={metaWalletCard}
                    alt=""
                    role="presentation"
                    className="absolute right-[-50px] top-[-2px] w-[421px] h-full object-contain"
                  />
                </div>

                <div className="relative z-10 h-full max-w-[620px] px-[64px] pt-[64px] pb-[64px] flex flex-col justify-between">
                  <div className="space-y-5">
                    <h2 className="m-0 text-white text-[40px] font-semibold leading-[1.15]">
                      Meta ads builder
                    </h2>
                    {!isAdminAccessPlan && (
                      <button
                        type="button"
                        onClick={() => {
                          if (shouldShowBillingDetailsCta) {
                            setShowBillingDetails(true);
                          } else if (cancelAtPeriodEnd) {
                            handleRenewSubscription();
                          } else {
                            navigate(ROUTES.META_ADS_SETUP_PUBLIC);
                          }
                        }}
                        disabled={isCreatingPortalSession}
                        className="w-auto px-[24px] py-[14px] rounded-[9999px] bg-white text-black text-[16px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {primaryCtaLabel}
                      </button>
                    )}
                  </div>

                  {hasActiveSubscription ? (
                    <div className="max-w-[640px]">
                      {isAdminAccessPlan ? (
                        <div className="space-y-2">
                          <p className="text-white text-[16px] leading-[1.35]">
                            <span className="font-bold">Current plan:</span>{" "}
                            <span className="font-bold">Custom</span>
                          </p>
                          {adminAccessEndDateLabel && (
                            <p className="text-white text-[16px] font-medium leading-[1.35]">
                              Ends on {adminAccessEndDateLabel}
                            </p>
                          )}
                        </div>
                      ) : cancelAtPeriodEnd ? (
                        <div className="space-y-11">
                          <div className="text-white leading-[1.55]">
                            <div className="text-[16px]">
                              <span className="block whitespace-nowrap">
                                Your subscription has been canceled. You can
                                renew it anytime
                              </span>
                              <span className="block whitespace-nowrap">
                                without charge to continue using it in the next
                                billing period.
                              </span>
                            </div>
                          </div>
                          {accessUntilDate && (
                            <p className="text-white text-[16px] leading-[1.55]">
                              You can continue using the tool until{" "}
                              <span className="inline">{accessUntilDate}</span>
                            </p>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <p className="text-white text-[16px] leading-[1.35]">
                              <span className="font-bold">Current plan:</span>{" "}
                              <span className="font-bold">{planLabel}</span>
                            </p>
                            {renewDate && (
                              <p className="text-white text-[16px] font-medium leading-[1.35]">
                                Automatically renew on {renewDate}
                              </p>
                            )}
                          </div>
                          <div className="mt-3 pt-1 flex items-center gap-5">
                            <button
                              type="button"
                              onClick={() => setIsChangePlanModalOpen(true)}
                              className="text-[#58F2A9] text-[16px] leading-[1.3] font-semibold"
                            >
                              Change plan
                            </button>
                            <span className="w-px h-8 bg-white/30" />
                            <button
                              type="button"
                              onClick={() => setIsCancelModalOpen(true)}
                              className="text-[#FF2D2D] text-[16px] leading-[1.3] font-semibold"
                            >
                              Cancel plan
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <p className="text-white text-[16px] leading-[1.5] font-medium max-w-[560px]">
                      Make ad management effortless with Meta Ads Builder and
                      reach new listeners using music industry - trusted
                      strategy!
                    </p>
                  )}
                </div>
              </div>
            </>
          </div>
        )}

        {hasActiveSubscription && showBillingDetails && (
          <>
            {isBillingFetching ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 border-4 border-t-white border-r-white border-b-transparent border-l-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <MetaAdsBillingDetails
                billingName={billingData?.billingName || ""}
                billingAddress={billingData?.billingAddress || ""}
                email={billingData?.email || ""}
                invoices={billingData?.invoices || []}
                onUpdateBilling={() => setIsUpdateBillingModalOpen(true)}
                userId={metaUserId}
                className="mt-0"
              />
            )}
          </>
        )}
      </div>
      <MetaAdsChangePlanModal
        isOpen={isChangePlanModalOpen}
        currentPlan={planType}
        onClose={() => setIsChangePlanModalOpen(false)}
        onConfirm={handleConfirmPlanChange}
        isLoading={isPlanChangeLoading}
      />

      <MetaAdsCancelPlanModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelSubscription}
        isLoading={isCancelLoading}
      />

      <MetaAdsUpdateBillingModal
        isOpen={isUpdateBillingModalOpen}
        onClose={() => setIsUpdateBillingModalOpen(false)}
        onSave={handleUpdateBilling}
        currentData={{
          billingName: billingData?.billingName || "",
          email: billingData?.email || "",
        }}
        isLoading={isBillingUpdating}
      />
    </div>
  );
}
